import React from 'react'
import { motion } from 'framer-motion'
import { AlertTriangle, RefreshCw } from 'lucide-react'

interface ErrorPageProps {
  title: string
  message: string
  showRetry?: boolean
  onRetry?: () => void
}

const ErrorPage: React.FC<ErrorPageProps> = ({ 
  title, 
  message, 
  showRetry = false, 
  onRetry 
}) => {
  const handleRetry = () => {
    if (onRetry) {
      onRetry()
    } else {
      // Default retry: reload the page
      window.location.reload()
    }
  }

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4"
      style={{
        backgroundColor: 'var(--bg-primary)',
        color: 'var(--text-primary)'
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-md w-full text-center"
      >
        {/* Error Icon */}
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, duration: 0.5 }}
          className="mb-6"
        >
          <div 
            className="w-20 h-20 mx-auto rounded-full flex items-center justify-center"
            style={{
              backgroundColor: 'rgba(239, 68, 68, 0.1)',
              border: '2px solid rgba(239, 68, 68, 0.3)'
            }}
          >
            <AlertTriangle 
              className="w-10 h-10" 
              style={{ color: '#ef4444' }}
            />
          </div>
        </motion.div>

        {/* Error Title */}
        <motion.h1
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4, duration: 0.5 }}
          className="text-2xl font-bold mb-4"
          style={{ color: 'var(--text-primary)' }}
        >
          {title}
        </motion.h1>

        {/* Error Message */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6, duration: 0.5 }}
          className="text-lg mb-8"
          style={{ color: 'var(--text-secondary)' }}
        >
          {message}
        </motion.p>

        {/* Action Buttons */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8, duration: 0.5 }}
          className="space-y-4"
        >
          {showRetry && (
            <motion.button
              onClick={handleRetry}
              className="inline-flex items-center px-6 py-3 rounded-lg font-medium transition-all duration-300 hover:scale-105"
              style={{
                backgroundColor: 'var(--primary)',
                color: 'white'
              }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <RefreshCw className="w-5 h-5 mr-2" />
              Tentar Novamente
            </motion.button>
          )}

          <motion.button
            onClick={() => window.close()}
            className="inline-flex items-center px-6 py-3 rounded-lg font-medium transition-all duration-300 hover:scale-105 ml-4"
            style={{
              backgroundColor: 'rgba(239, 68, 68, 0.1)',
              color: '#ef4444',
              border: '1px solid rgba(239, 68, 68, 0.3)'
            }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Fechar Janela
          </motion.button>
        </motion.div>

        {/* Additional Info */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1, duration: 0.5 }}
          className="mt-8 text-sm"
          style={{ color: 'var(--text-tertiary)' }}
        >
          <p>
            Se o problema persistir, entre em contato com o suporte técnico.
          </p>
          <p className="mt-2">
            <a 
              href="mailto:lognex@lognex.com.br"
              className="underline hover:no-underline"
              style={{ color: 'var(--primary)' }}
            >
              lognex@lognex.com.br
            </a>
          </p>
        </motion.div>
      </motion.div>
    </div>
  )
}

export default ErrorPage
